import { useEffect, useMemo, useState } from 'react'
import { MINIMAP_BYTES, wsRoom, type MinimapFrame } from '@apiary/shared'
import { getWsClient } from '@/lib/ws'

/**
 * Мини-карта (L1, docs/08 §4).
 *
 * Кадры приезжают в комнату `bot:<id>`. Отдельной сессии у мини-карты нет: она
 * не держит `WorldView`, не имеет `viewDistance` и стоит ~4 КБ/с. Runner считает
 * срез, только пока в комнате есть подписчик, — это решает API по составу
 * комнаты, поэтому подписка здесь и есть «включатель».
 */

/** Один бот (карточка бота — комната уже подписана через `useWsRoom`). */
export function useMinimap(botId: string | null): MinimapFrame | null {
  const ids = useMemo(() => (botId === null ? [] : [botId]), [botId])
  const frames = useMinimaps(ids)
  return botId === null ? null : (frames[botId] ?? null)
}

/**
 * Много ботов (сетка карточек). Подписывается на комнаты всех переданных ботов:
 * docs/08 §1 — «L1 всегда для PLAYING», это дешёвая часть камеры.
 */
export function useMinimaps(botIds: readonly string[]): Record<string, MinimapFrame> {
  const [frames, setFrames] = useState<Record<string, MinimapFrame>>({})
  // Ключ по составу списка: массив пересоздаётся на каждый рендер родителя, и без
  // этого подписки пересоздавались бы вместе с ним.
  const key = botIds.join(',')

  useEffect(() => {
    const ids = key === '' ? [] : key.split(',')
    const client = getWsClient()
    if (client === null || ids.length === 0) return
    for (const id of ids) {
      void client.subscribe(wsRoom.bot(id)).catch(() => {
        // Нет прав или бот исчез — мини-карта просто не появится.
      })
    }
    let alive = true
    const off = client.on('viewer.minimap', (...args: unknown[]) => {
      void decodeMinimap(args[0]).then(decoded => {
        if (!alive || decoded === null || !ids.includes(decoded.botId)) return
        setFrames(previous => ({ ...previous, [decoded.botId]: decoded }))
      })
    })
    return () => {
      alive = false
      off()
      for (const id of ids) client.release(wsRoom.bot(id))
    }
  }, [key])

  return frames
}

/**
 * Декодер msgpack подгружается динамически.
 *
 * Мини-карта работает всегда (docs/08 §1), но декодер весит ~8 КБ gzip, и
 * попадать во входной бандл он не должен: NFR-6 упирается в вес входа, а первый
 * срез приходит только через `MINIMAP_INTERVAL_MS` после подписки — асинхронный
 * импорт к тому моменту давно завершён.
 */
let decoder: Promise<(bytes: Uint8Array) => unknown> | null = null

function loadDecoder(): Promise<(bytes: Uint8Array) => unknown> {
  decoder ??= import('@msgpack/msgpack').then(module => module.decode)
  return decoder
}

async function decodeMinimap(raw: unknown): Promise<MinimapFrame | null> {
  const bytes =
    raw instanceof Uint8Array ? raw : raw instanceof ArrayBuffer ? new Uint8Array(raw) : null
  let value: unknown = raw
  if (bytes !== null) {
    const decode = await loadDecoder()
    value = safeDecode(decode, bytes)
  }
  if (value === null || typeof value !== 'object') return null
  const frame = value as Partial<MinimapFrame>
  if (typeof frame.botId !== 'string' || !(frame.data instanceof Uint8Array)) return null
  // Кадр не того размера — это рассинхрон версий, а не «немного другой мир».
  if (frame.data.byteLength !== MINIMAP_BYTES) return null
  return {
    botId: frame.botId,
    originX: frame.originX ?? 0,
    originZ: frame.originZ ?? 0,
    baseY: frame.baseY ?? 0,
    data: frame.data
  }
}

function safeDecode(decode: (bytes: Uint8Array) => unknown, bytes: Uint8Array): unknown {
  try {
    return decode(bytes)
  } catch {
    return null
  }
}
